import { useState, useCallback } from 'react';
import { formatPrice } from '../../utils/formatPrice';
import api from '../../api/client';

/**
 * Format an ISO date string for display.
 *
 * @param {string} dateString - ISO 8601 date string from the API.
 * @returns {string} Human-readable date/time.
 */
function formatDate(dateString) {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

/**
 * Order confirmation modal shown after a successful checkout.
 *
 * @param {Object}   props
 * @param {Object}   props.order          - Order object returned from the API
 * @param {Function} props.onPrintInvoice - Handler for "Print Invoice" action
 * @param {Function} props.onPrintReceipt - Handler for "Print Receipt" action
 * @param {Function} props.onNewSale      - Handler for "New Sale" action
 */
export default function OrderConfirmation({
  order,
  onPrintInvoice,
  onPrintReceipt,
  onNewSale,
  crmEnabled = false,
}) {
  const [crmSyncing, setCrmSyncing] = useState(false);
  const [crmResult, setCrmResult] = useState(null); // { created: bool } or null

  const handleSyncToCrm = useCallback(async () => {
    if (!order?.customer?.email) return;
    setCrmSyncing(true);
    try {
      const result = await api.syncContactToCrm({
        customer_name: order.customer.name || '',
        customer_email: order.customer.email,
        customer_phone: order.customer.phone || '',
      });
      setCrmResult(result);
    } catch {
      setCrmResult({ error: true });
    } finally {
      setCrmSyncing(false);
    }
  }, [order]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onNewSale();
      }
    },
    [onNewSale]
  );

  if (!order) return null;

  const subtotal = parseFloat(order.subtotal) || 0;
  const discount = parseFloat(order.discount_total) || 0;
  const tax = parseFloat(order.total_tax) || 0;
  const total = parseFloat(order.total) || 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Order confirmation"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* ---- Scrollable content ---- */}
        <div className="flex-1 overflow-y-auto">
          {/* -- Success header -- */}
          <div className="flex flex-col items-center pt-8 pb-4 px-6">
            {/* Green checkmark circle */}
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-8 h-8 text-pos-success"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            <h2 className="text-xl font-bold text-gray-900">
              Order #{order.number}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Sale completed successfully
            </p>
          </div>

          {/* -- Order details -- */}
          <div className="px-6 pb-4">
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Date</span>
                <span className="text-gray-900 font-medium">
                  {formatDate(order.date_created)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Payment</span>
                <span className="text-gray-900 font-medium">
                  {order.payment_title || order.payment_method || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Customer</span>
                <span className="text-gray-900 font-medium">
                  {order.customer?.name || 'Guest'}
                </span>
              </div>
            </div>
          </div>

          {/* -- Items table -- */}
          <div className="px-6 pb-4">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
              Items
            </h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">
                      Item
                    </th>
                    <th className="text-center px-2 py-2.5 font-medium text-gray-600 w-14">
                      Qty
                    </th>
                    <th className="text-right px-3 py-2.5 font-medium text-gray-600 w-24">
                      Price
                    </th>
                    <th className="text-right px-4 py-2.5 font-medium text-gray-600 w-24">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(order.items || []).map((item, idx) => {
                    const lineTotal = parseFloat(item.total) || 0;
                    const unitPrice = parseFloat(item.price) || 0;

                    return (
                      <tr key={item.id || idx}>
                        <td className="px-4 py-2.5 text-gray-900">
                          <span className="block font-medium leading-tight">
                            {item.name}
                          </span>
                          {item.sku && (
                            <span className="block text-xs text-gray-400 mt-0.5">
                              SKU: {item.sku}
                            </span>
                          )}
                        </td>
                        <td className="text-center px-2 py-2.5 text-gray-600">
                          {item.quantity}
                        </td>
                        <td className="text-right px-3 py-2.5 text-gray-600">
                          {formatPrice(unitPrice)}
                        </td>
                        <td className="text-right px-4 py-2.5 text-gray-900 font-medium">
                          {formatPrice(lineTotal)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* -- Totals -- */}
          <div className="px-6 pb-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal</span>
                <span>{formatPrice(subtotal)}</span>
              </div>

              {discount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount</span>
                  <span>-{formatPrice(discount)}</span>
                </div>
              )}

              {tax > 0 && (
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Tax</span>
                  <span>{formatPrice(tax)}</span>
                </div>
              )}

              <div className="border-t border-gray-200 pt-2 mt-2">
                <div className="flex justify-between items-baseline">
                  <span className="text-base font-semibold text-gray-900">Total</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {formatPrice(total)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ---- Action buttons ---- */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-3">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onPrintInvoice}
              className="flex-1 px-4 py-2.5 rounded-lg bg-pos-primary text-white text-sm font-medium hover:bg-pos-primary-dark transition-colors flex items-center justify-center gap-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-4 h-4"
              >
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              Print Invoice
            </button>
            <button
              type="button"
              onClick={onPrintReceipt}
              className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-4 h-4"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              Print Receipt
            </button>
          </div>
          {crmEnabled && order?.customer?.email && (
            <div className="flex items-center gap-2">
              {crmResult ? (
                <p className="text-sm text-center w-full py-1">
                  {crmResult.error ? (
                    <span className="text-red-600">Failed to sync contact</span>
                  ) : crmResult.created ? (
                    <span className="text-green-600">Contact synced to CRM</span>
                  ) : (
                    <span className="text-gray-500">Contact already exists in CRM</span>
                  )}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleSyncToCrm}
                  disabled={crmSyncing}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {crmSyncing ? (
                    'Syncing...'
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                      Sync to CRM
                    </>
                  )}
                </button>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={onNewSale}
            className="w-full px-4 py-3 rounded-lg bg-pos-foreground text-white text-sm font-semibold hover:bg-pos-foreground/80 transition-colors flex items-center justify-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-4 h-4"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
            Exit
          </button>
        </div>
      </div>
    </div>
  );
}
